import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CaptchaService {
  private readonly SCRIPT_URL =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  // Cache the load so concurrent callers share one injection. A boolean that
  // only flips inside onload lets two callers racing before the first script
  // finishes each append api.js — two live Turnstile runtimes on one page is
  // unsupported and surfaces as endless error 600010.
  private loadPromise: Promise<void> | undefined;
  private scriptEl: HTMLScriptElement | undefined;
  private renderer: Renderer2;

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Drops the current Turnstile runtime and loads a brand-new api.js.
   * Last-resort self-heal for the retry after a challenge died with a
   * client-side execution error. Safe because widgets are single-use —
   * nothing is alive between challenges, so the old runtime is inert once its
   * script tag and global are gone.
   */
  async refreshRuntime(): Promise<void> {
    if (this.scriptEl?.parentNode) {
      this.renderer.removeChild(this.scriptEl.parentNode, this.scriptEl);
    }
    this.scriptEl = undefined;
    this.loadPromise = undefined;
    // Without this the runtime-already-present shortcut in loadCaptcha() would
    // resolve against the old global instead of initialising a fresh one.
    delete (window as any).turnstile;
    await this.loadCaptcha();
  }

  loadCaptcha(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<void>((resolve, reject) => {
      // Runtime already present (a prior load, or HMR) — reuse it, never inject
      // a second api.js.
      if (typeof (window as any).turnstile?.render === 'function') {
        resolve();
        return;
      }

      const script = this.renderer.createElement('script');
      this.renderer.setAttribute(script, 'src', this.SCRIPT_URL);
      this.renderer.setAttribute(script, 'async', 'true');
      this.renderer.setAttribute(script, 'defer', 'true');

      script.onload = () => resolve();

      script.onerror = (error: unknown) => {
        // Drop the cache so a later attempt can retry the load rather than
        // reusing a permanently-rejected promise.
        this.loadPromise = undefined;
        this.scriptEl = undefined;
        reject(error);
      };

      this.scriptEl = script;
      this.renderer.appendChild(document.head, script);
    });

    return this.loadPromise;
  }
}
